import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Loader2, Search, History, Database } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

const AuditLogViewer: React.FC = () => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/reporting/audit-logs');
            if (res.ok) setLogs(await res.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredLogs = logs.filter(log =>
        log.table_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">System Audit Logs</h2>
                    <p className="text-gray-500">Track all critical state changes and data mutations</p>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search logs..."
                        className="pl-10 pr-4 py-2 border rounded-xl w-64 focus:ring-2 focus:ring-blue-500 outline-none transition"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50/50">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center w-20">Type</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Table / Action</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Record ID</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Timestamp</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredLogs.map((log, i) => (
                            <tr key={i} className="hover:bg-gray-50 group">
                                <td className="px-6 py-4">
                                    <div className={`p-2 rounded-lg flex items-center justify-center ${log.action === 'INSERT' ? 'bg-emerald-50 text-emerald-600' :
                                        log.action === 'UPDATE' ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'
                                        }`}>
                                        <Database size={16} />
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-800 uppercase text-[10px] tracking-wider mb-1">
                                        {log.table_name.replace('dv_', '')}
                                    </div>
                                    <div className={`text-sm font-bold ${log.action === 'DELETE' ? 'text-red-600' : 'text-gray-700'
                                        }`}>
                                        {log.action}
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <code className="text-[10px] bg-gray-50 px-2 py-1 rounded text-gray-400">{log.record_id}</code>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                    {new Date(log.created_at).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button className="p-2 text-gray-400 hover:text-blue-600 transition">
                                        <History size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center gap-4 p-6 bg-blue-50 rounded-3xl border border-blue-100">
                <div className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-100">
                    <ShieldCheck size={24} />
                </div>
                <div>
                    <h4 className="font-bold text-blue-900">Security Note</h4>
                    <p className="text-blue-700 text-sm">Audit logs are immutable and stored with cryptographic integrity. Only Super Admins can access this view.</p>
                </div>
            </div>
        </div>
    );
};

export default AuditLogViewer;
